/**
 * useMemberSelection Hook - Story 4.5
 *
 * @deprecated This hook violates the Controller pattern by using internal React hooks.
 * Use useMemberSelectionController instead, which provides pure state management tools.
 *
 * Migration:
 * - Import: `import { useMemberSelectionController } from '@/hooks-v2';`
 * - Use with useReducer: `const [state, dispatch] = useReducer(controller.reducer, null, controller.createInitialState);`
 * - Fetch members separately with useGetMembers
 * - Dispatch actions: `dispatch(controller.actions.toggleSelection(userId));`
 * - Use selectors: `controller.selectors.getSummary(state);`
 *
 * This hook will be removed in a future version.
 *
 * ---
 * Original description:
 * Manages member selection state for coupon distribution
 * Used in Step 2 of the coupon creation wizard
 *
 * Features:
 * - Fetches members using generated hook
 * - Tracks selected members via Set<string>
 * - Manages default quantity for all selections
 * - Provides summary calculations
 * - Validates selection before proceeding
 */

import { useState, useMemo, useCallback } from 'react';
import { useListMembers } from '@/generated/hooks';
import type { MemberSelectionSummary, UseMemberSelectionReturn } from '@/types/ui.types';

/**
 * Hook for managing member selection during coupon distribution
 *
 * @param merchantId - The merchant ID to fetch members for
 * @returns Member selection state and handlers
 */
export function useMemberSelection(merchantId: number): UseMemberSelectionReturn {
	// 1. Fetch members using generated hook
	const { data: membersResponse, isLoading } = useListMembers(
		{ merchantId },
		{ enabled: merchantId > 0 },
	);

	// Extract items from response, map to required fields only
	// Note: Hook returns array directly, not { items: [...] }
	const members = useMemo(() => {
		const items = membersResponse ?? [];
		return items.map((item) => ({
			user_id: item.user_id,
			display_name: item.display_name,
			phone_masked: item.phone_masked,
		}));
	}, [membersResponse]);

	// 2. Local state for selection (simple Set, not Map)
	const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
	const [defaultQuantity, setDefaultQuantity] = useState(1);

	// 3. Toggle selection handler
	const handleToggleSelection = useCallback((userId: string) => {
		setSelectedUserIds((prev) => {
			const next = new Set(prev);
			if (next.has(userId)) {
				next.delete(userId);
			} else {
				next.add(userId);
			}
			return next;
		});
	}, []);

	// 4. Summary calculation
	// V1.1: One coupon per member (totalCouponInstances = totalMembersSelected)
	const summary = useMemo<MemberSelectionSummary>(
		() => ({
			totalMembersSelected: selectedUserIds.size,
			totalCouponInstances: selectedUserIds.size, // V1.1: Always 1 per member
		}),
		[selectedUserIds.size],
	);

	// 5. Validation
	const isValid = selectedUserIds.size > 0 && defaultQuantity >= 1;

	// 6. Get distribution data for API (Story 4.6 integration)
	// V1.1: Hardcode instance_count to 1 (one coupon per member with unlimited sharing)
	const getDistributionData = useCallback(
		() =>
			Array.from(selectedUserIds).map((userId) => ({
				user_id: userId,
				instance_count: 1, // V1.1: Always 1 per member
			})),
		[selectedUserIds],
	);

	// 7. Reset selection
	const resetSelection = useCallback(() => {
		setSelectedUserIds(new Set());
		setDefaultQuantity(1);
	}, []);

	return {
		// Data
		members,
		selectedUserIds,
		defaultQuantity,
		summary,

		// State
		isLoading,
		isValid,

		// Actions
		handleToggleSelection,
		setDefaultQuantity,
		getDistributionData,
		resetSelection,
	};
}

export default useMemberSelection;
